import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Box,
  Grid,
  GridItem,
  Heading,
  HStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
} from "@chakra-ui/react";
import { GraphQLError } from "graphql";
import { groupBy, uniq } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { api } from "src/api";
import {
  Mutation,
  Query,
  TransactionTypeOption,
  TransactionTypeOptionResponse,
} from "src/api/generated/types";
import { hasValue, Maybe } from "src/core";
import { useMyToast } from "src/hooks";
import { useIsLargeScreen } from "src/hooks/useScreenSize";
import { useTheme } from "src/hooks/useTheme";
import { colors } from "src/theme";
import { Input } from "../styled";
import { AwakenTooltip } from "../styled/AwakenTooltip";
import StatusTag from "../styled/StatusTag";
import { Touchable } from "../Touchable";

type ReviewStatusTagProps = {
  isReviewed: boolean;
  capPriority?: Maybe<number>;
  needsRecalculate: boolean;
  transactionId?: string;
  type?: string | null;
  currentLabelUsed?: Maybe<string>;
  defaultLabels?: Maybe<TransactionTypeOptionResponse>;
};

export const ReviewStatusTag = React.memo(
  ({
    isReviewed,
    capPriority,
    type,
    currentLabelUsed,
    transactionId,
    needsRecalculate,
    defaultLabels,
  }: ReviewStatusTagProps) => {
    if (needsRecalculate) {
      return (
        <AwakenTooltip openDelay={250} label={"Run recalculate to update this"}>
          <div>
            <StatusTag
              iconName="fa-sharp fa-question-circle"
              type={"info"}
              boxProps={{ style: { flexWrap: "nowrap" } }}
              label="Needs Recalculate"
            />
          </div>
        </AwakenTooltip>
      );
    } else if (isReviewed) {
      return (
        <UpdatableReviewTag
          iconName="fa-sharp fa-check"
          type="none"
          label={"Reviewed"}
          capPriority={capPriority}
          transactionId={transactionId}
          processingType={type}
          currentLabelUsed={currentLabelUsed}
          needsReview={false}
          defaultLabels={defaultLabels}
        />
      );
    } else {
      return (
        <UpdatableReviewTag
          label="Needs Review"
          iconName="fa-sharp fa-chevron-down"
          type={
            capPriority === 3 ? "error" : capPriority === 2 ? "warning" : "none"
          }
          capPriority={capPriority}
          transactionId={transactionId}
          needsReview={true}
          defaultLabels={defaultLabels}
        />
      );
    }
  }
);

const UpdatableReviewTag = React.memo(
  ({
    capPriority,
    transactionId,
    iconName,
    type,
    label,
    currentLabelUsed,
    processingType,
    needsReview,
    defaultLabels,
  }: {
    capPriority?: Maybe<number>;
    transactionId?: string;
    iconName: string;
    type: any;
    label: string;
    currentLabelUsed?: Maybe<string>;
    processingType?: Maybe<string>;
    needsReview?: boolean;
    defaultLabels?: Maybe<TransactionTypeOptionResponse>;
  }) => {
    const toast = useMyToast();
    const theme = useTheme();

    const [search, setSearch] = useState("");
    const [aiNotes, setAINotes] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [aiSelectedLabel, setAiSelectedLabel] =
      useState<TransactionTypeOption | null>(null);
    const onClose = () => setIsOpen(false);

    const [updateTransaction, { loading: isLoadingUpdateTransaction }] =
      useMutation(api.transactions.update);

    const [getLabelOptions, { data: data }] = useLazyQuery<
      Pick<Query, "getTransactionTypeOptions">
    >(api.transactions.typeOptions);

    const onSelectOption = async (option: Maybe<TransactionTypeOption>) => {
      if (!option) return;

      try {
        if (!option.applicable) {
          return;
        }

        const variables = {
          updates: {
            label: option.value,
            overrideLabel: true,
          },
          transactionId: transactionId,
          createDefaultRule: false,
        };

        setIsOpen(false);

        await updateTransaction({
          variables,
          refetchQueries: [
            api.transactions.retrieve,
            api.clients.transactions,
            api.transactions.countTransactions,
            api.transactions.getNumTxnTypes,
            api.graph.getReplayInfo,
          ],
        });

        toast.show({
          message: `Successfully labeled transaction as ${option.label}`,
          status: "success",
        });
      } catch (err) {
        toast.show({
          message:
            (err as GraphQLError).message ||
            "There was an error updating the transaction.",
          status: "error",
        });
      }
    };

    const _getOptions = async (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      try {
        setIsOpen(true);

        await getLabelOptions({
          variables: {
            transactionId: transactionId,
          },
        });
      } catch (err) {
        // Error handling is optional here
      }
    };

    const _labelOptions =
      data?.getTransactionTypeOptions.labels || defaultLabels?.labels || [];

    const labelOptions = useMemo(() => {
      return _labelOptions.filter((o) => {
        if (!search && aiSelectedLabel) {
          return aiSelectedLabel.value === o.value;
        }

        return o.label.toLowerCase().includes(search.toLowerCase());
      });
    }, [_labelOptions, search, aiSelectedLabel]);

    const categories = useMemo(
      () => (!labelOptions ? [] : uniq(labelOptions.map((o) => o.category))),
      [labelOptions]
    );

    const optionsByCategory = useMemo(
      () => groupBy(labelOptions, (o: TransactionTypeOption) => o.category),
      [labelOptions]
    );

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (isOpen && inputRef.current) {
        inputRef.current.focus();
      }
    }, [isOpen]);

    return (
      <Popover isLazy placement="bottom" isOpen={isOpen} onClose={onClose}>
        <PopoverTrigger>
          <Box>
            <AwakenTooltip openDelay={250} message={processingType}>
              <div>
                {label === "Reviewed" ? (
                  <HStack>
                    <Text w="100%" fontSize="sm" color={theme.text}>
                      Reviewed{" "}
                    </Text>
                    <i
                      style={{ marginLeft: 5, color: theme.text }}
                      className="fa-solid fa-check-circle"
                    />
                  </HStack>
                ) : (
                  <StatusTag
                    iconName={iconName}
                    onClick={(e) => {
                      if (isOpen) {
                        onClose();
                      } else {
                        _getOptions(e);
                      }
                    }}
                    type={type}
                    iconStyle={{
                      color: !needsReview ? theme.text : undefined,
                    }}
                    labelStyle={{
                      fontWeight: !needsReview ? "600" : undefined,
                      fontSize: 14,
                      color: !needsReview ? theme.text : undefined,
                    }}
                    boxProps={{
                      style: {
                        flexWrap: "nowrap",
                        border: "none",
                      },
                    }}
                    label={label}
                  />
                )}
              </div>
            </AwakenTooltip>
          </Box>
        </PopoverTrigger>
        <PopoverContent
          width={275}
          paddingTop={"10px"}
          overflowY="hidden"
          overflowX="hidden"
          style={{
            paddingTop: 10,
            border: `1px solid ${theme.border}`,
            backgroundColor: theme.background,
            boxShadow:
              theme.theme === "dark"
                ? `0 0 10px 0 ${theme.border}`
                : `0 0 10px 0 ${theme.ternaryBackground}`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Input
            ref={inputRef}
            autoFocus
            iconLeft={
              <i
                className="fa-solid fa-magnifying-glass"
                style={{
                  marginLeft: 15,
                  position: "relative",
                  top: -2,
                  color: colors.gray60,
                }}
              />
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            focusBorderColor={"transparent"}
            style={{
              border: `1px solid ${theme.border}`,
              margin: "0 10px",
              height: 35,
              backgroundColor: theme.secondaryBackground,
              paddingLeft: 30,
              fontSize: 14,
              marginBottom: 10,
            }}
            containerStyle={{ marginBottom: 0 }}
            placeholder="Search labels"
          />

          <Grid
            paddingBottom="0"
            templateColumns={{
              base: "repeat(1, 1fr)",
            }}
            style={{
              borderTop: `1px solid ${theme.border}`,
              overflowY: "scroll",
              padding: 5,
              height: aiSelectedLabel && categories.length === 1 ? "auto" : 250,
            }}
          >
            {categories.map((category) => (
              <CategorySection
                key={category}
                selectedOptionValue={currentLabelUsed}
                onSelectOption={onSelectOption}
                category={category}
                options={optionsByCategory[category]}
                aiSelectedLabel={aiSelectedLabel}
              />
            ))}
          </Grid>

          {aiNotes && (
            <Text
              style={{
                padding: "0 10px 20px 10px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                cursor: "text",
              }}
              fontSize="sm"
              color={theme.text}
            >
              {aiNotes}
            </Text>
          )}

          <HStack
            style={{
              borderTop: `1px solid ${theme.border}`,
              padding: 10,
              backgroundColor: theme.medBackground,
            }}
          >
            <div style={{ flex: 1 }}>
              <AIHelper
                transactionId={transactionId || ""}
                defaultLabels={defaultLabels ?? null}
                aiNotes={aiNotes}
                setAiNotes={setAINotes}
                aiSelectedLabel={aiSelectedLabel}
                setAiSelectedLabel={setAiSelectedLabel}
              />
            </div>

            <div
              onClick={onClose}
              style={{
                alignItems: "center",
                justifyContent: "center",
                color: theme.text,
                textAlign: "right",
              }}
            >
              Close{" "}
              <i style={{ fontSize: 12 }} className="fa-sharp fa-eye-slash" />
            </div>
          </HStack>
        </PopoverContent>
      </Popover>
    );
  }
);

const CategorySection = React.memo(
  ({
    category,
    options,
    selectedOptionValue,
    onSelectOption,
    aiSelectedLabel,
  }: {
    category: string;
    options: TransactionTypeOption[];
    selectedOptionValue?: Maybe<string>;
    onSelectOption: (option: TransactionTypeOption) => void;
    aiSelectedLabel: TransactionTypeOption | null;
  }) => {
    const theme = useTheme();

    if (!options || options.length === 0) return null;
    return (
      <GridItem
        colSpan={1}
        position="relative"
        key={category}
        style={{
          marginBottom: 15,
          paddingTop: 15,
          textAlign: "left",
          paddingBottom: 10,
          borderBottom: `1px solid ${theme.border}`,
        }}
      >
        <Heading
          color={theme.text}
          fontWeight="black"
          textTransform="uppercase"
          marginTop="0rem"
          padding={"0 7px"}
          marginBottom={"0.5rem"}
          fontSize={10}
        >
          {category}
        </Heading>
        {options.map((o) => {
          const isSelected = selectedOptionValue === o.value;
          const isAIRecommended = aiSelectedLabel?.value === o.value;

          return (
            <Box
              _hover={{
                backgroundColor: theme.secondaryBackground,
              }}
              onClick={() => onSelectOption(o)}
              isDisabled={!o.applicable}
              bg={
                isAIRecommended
                  ? theme.greenBg
                  : isSelected
                  ? theme.greenBg
                  : undefined
              }
              style={{
                borderRadius: 10,
                opacity: o.applicable ? 1 : 0.33,
                padding: 7,
                color: !o.applicable ? colors.gray4 : theme.header,
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
              }}
              key={o.value}
            >
              {isAIRecommended && (
                <i
                  className="fa-solid fa-arrow-right"
                  style={{ color: colors.positive, marginRight: 5 }}
                />
              )}

              <span style={{ flex: 1 }}>{o.label}</span>

              {isSelected && (
                <i
                  className="fa-solid fa-check"
                  style={{ color: colors.positive }}
                />
              )}

              {isAIRecommended && (
                <AwakenTooltip
                  label="Recommended by Awaken AI. Please make sure to verify before submitting."
                  placement="top"
                >
                  <i
                    className="fa-solid fa-circle-info"
                    style={{ color: colors.positive }}
                  />
                </AwakenTooltip>
              )}
            </Box>
          );
        })}
      </GridItem>
    );
  }
);

const AIHelper = ({
  transactionId,
  defaultLabels,
  aiNotes,
  setAiNotes,
  aiSelectedLabel,
  setAiSelectedLabel,
}: {
  transactionId: string;
  defaultLabels: Maybe<TransactionTypeOptionResponse>;
  aiNotes: string | null;
  setAiNotes: (aiNotes: string | null) => void;
  aiSelectedLabel: TransactionTypeOption | null;
  setAiSelectedLabel: (aiSelectedLabel: TransactionTypeOption | null) => void;
}) => {
  const [summarizeTransaction] = useMutation<
    Pick<Mutation, "summarizeTransaction">
  >(api.transactions.summarize);

  const [hasUsedAi, setHasUsedAi] = useState(false);
  const toast = useMyToast();
  const theme = useTheme();

  const [isSummarizing, setIsSummarizing] = useState(false);
  const isLarge = useIsLargeScreen();
  const dispatch = useDispatch();

  const _summarize = async () => {
    try {
      if (isSummarizing) {
        return;
      }

      if (!transactionId) {
        return;
      }

      setIsSummarizing(true);

      const response = await summarizeTransaction({
        variables: {
          transactionId: transactionId,
        },
      });

      let notes = "";
      const label = response.data?.summarizeTransaction?.label;
      const description = response.data?.summarizeTransaction?.description;
      const analysis = response.data?.summarizeTransaction?.analysis;

      if ((description || analysis) && label) {
        setHasUsedAi(true);

        notes += [
          description ? `Description: ${description}` : null,
          analysis ? `Analysis: ${analysis}` : null,
          `Recommended: ${label}`,
        ]
          .filter(hasValue)
          .join("\n\n");
      }

      if (label) {
        const potentialLabel = (defaultLabels?.labels || []).find(
          (l: TransactionTypeOption) =>
            (l.label || "")
              ?.toLocaleLowerCase()
              .includes(label.toLocaleLowerCase()) ||
            l.value
              .toLocaleLowerCase()
              .includes(label?.toLocaleLowerCase() || "")
        );

        if (potentialLabel) {
          setAiSelectedLabel(potentialLabel);
        }
      }

      setAiNotes(notes);

      setHasUsedAi(true);
    } catch (error) {
      console.error(error);

      toast.show({
        message:
          (error as any)?.message ||
          "Error summarizing transaction. We've been alerted and will look into the issue!",
        status: "error",
      });
    } finally {
      setIsSummarizing(false);
    }
  };

  if (aiNotes) {
    return null;
  }

  return (
    <>
      <HStack
        style={{
          padding: 0,
          borderRadius: 15,
          background: "transparent",
        }}
      >
        <AwakenTooltip
          message="Please verify all labels before submitting. AI can hallucinate."
          placement="top"
        >
          <div style={{}}>
            <Touchable
              onClick={_summarize}
              style={{
                background: colors.primary,
                color: theme.text,
                padding: "5px 10px",
                borderRadius: 15,
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              }}
              isLoading={isSummarizing}
              labelStyle={{
                color: colors.white,
                fontWeight: "bold",
                fontSize: 12,
                fontStretch: "extra-expanded",
              }}
              iconStyle={{
                marginLeft: 5,
                color: colors.white,
                fontSize: 14,
              }}
              label="Ask AI"
              iconName="fa-sharp fa-wand-magic-sparkles"
            />
          </div>
        </AwakenTooltip>
      </HStack>
    </>
  );
};
